import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRightLeft, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const CurrencyConverter = () => {
  const { t } = useLanguage();
  const [amount, setAmount] = useState(100);
  const [inputValue, setInputValue] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('DOP');
  const [result, setResult] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Handle input change - allow empty string while typing
  const handleAmountChange = (e) => {
    const value = e.target.value;
    
    // Allow empty string or valid numbers
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setInputValue(value);
      const numValue = parseFloat(value);
      setAmount(isNaN(numValue) ? 0 : numValue);
    }
  };

  // Handle blur - ensure we have a valid number
  const handleAmountBlur = () => {
    if (inputValue === '' || isNaN(parseFloat(inputValue))) {
      setInputValue('0');
      setAmount(0);
    }
  };

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦' },
    { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
    { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$', flag: '🇩🇴' }
  ];

  // Fetch exchange rates
  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();
      
      // Store all rates relative to USD
      setRates({
        USD: 1,
        CAD: data.rates.CAD,
        EUR: data.rates.EUR,
        DOP: data.rates.DOP
      });
      
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      // Fallback rates if API fails
      setRates({
        USD: 1,
        CAD: 1.41,
        EUR: 0.85,
        DOP: 60.50
      });
      setLastUpdated('Using cached rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  useEffect(() => {
    if (rates && amount) {
      // Convert: amount in fromCurrency -> USD -> toCurrency
      const amountInUSD = amount / rates[fromCurrency];
      const converted = amountInUSD * rates[toCurrency];
      setResult(converted);
    }
  }, [amount, fromCurrency, toCurrency, rates]);

  const formatCurrency = (value, currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currencyCode === 'DOP') {
      return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2
      }).format(value);
    }
    return `${currency?.symbol || ''}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const getExchangeRate = (from, to) => {
    if (!rates) return null;
    const rateInUSD = 1 / rates[from];
    return rateInUSD * rates[to];
  };

  const selectedFromCurrency = currencies.find(c => c.code === fromCurrency);
  const selectedToCurrency = currencies.find(c => c.code === toCurrency);

  // Quick amounts based on selected currency
  const getQuickAmounts = () => {
    if (fromCurrency === 'DOP') {
      return [1000, 5000, 10000, 50000];
    }
    return [100, 500, 1000, 5000];
  };

  return (
    <section id="currency" className="py-16 bg-gradient-to-br from-[#22C55E]/10 via-white to-[#FF6B35]/10">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-10">
          <span className="inline-block text-white bg-[#22C55E] font-semibold tracking-wider uppercase text-sm mb-4 px-4 py-1 rounded-full">
            {t('currency.badge') || 'Currency Tool'}
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#000080] mb-4">
            {t('currency.title') || 'Currency Converter'}
          </h2>
          <p className="text-gray-600 text-lg">
            {t('currency.subtitle2') || 'Convert between USD, CAD, EUR and Dominican Pesos (DOP)'}
          </p>
        </div>

        {/* Converter card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 border border-gray-100">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-center">
            {/* From Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('currency.from') || 'From'}
              </label>
              <div className="flex gap-3">
                <div className="relative">
                  <select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    className="appearance-none bg-[#000080] text-white font-bold px-4 py-4 pr-10 rounded-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                  >
                    {currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.flag} {currency.code}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                <div className="relative flex-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                    {selectedFromCurrency?.symbol}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={inputValue}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                    className="w-full pl-12 pr-4 py-4 text-xl font-bold text-[#000080] border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#8B5CF6] transition-colors"
                  />
                </div>
              </div>

              {/* Quick amounts */}
              <div className="flex gap-2 mt-4 flex-wrap">
                {getQuickAmounts().map((quickAmount) => (
                  <button
                    key={quickAmount}
                    onClick={() => {
                      setAmount(quickAmount);
                      setInputValue(quickAmount.toString());
                    }}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      amount === quickAmount
                        ? 'bg-[#8B5CF6] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {quickAmount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Swap button */}
            <div className="flex justify-center">
              <button
                onClick={swapCurrencies}
                className="w-14 h-14 rounded-full bg-[#FF6B35] hover:bg-[#E85A2A] flex items-center justify-center transition-all hover:scale-110 shadow-lg"
                title="Swap currencies"
              >
                <ArrowRightLeft className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* To Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('currency.to') || 'To'}
              </label>
              <div className="flex gap-3">
                <div className="relative">
                  <select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    className="appearance-none bg-[#22C55E] text-white font-bold px-4 py-4 pr-10 rounded-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                  >
                    {currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.flag} {currency.code}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                <div className="relative flex-1">
                  <div className="w-full pl-4 pr-4 py-4 text-xl font-bold text-[#22C55E] bg-gray-50 border-2 border-gray-200 rounded-xl">
                    {loading ? (
                      <span className="text-gray-400">...</span>
                    ) : (
                      <span>{formatCurrency(result || 0, toCurrency)}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Exchange rate display */}
              <div className="mt-4 text-sm text-gray-500">
                {rates && (
                  <p>
                    1 {fromCurrency} = {getExchangeRate(fromCurrency, toCurrency)?.toFixed(4)} {toCurrency}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* All rates reference */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <p className="text-center text-sm text-gray-500 mb-4">
              {t('currency.currentRates') || 'Current Exchange Rates'}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {currencies.filter(c => c.code !== 'DOP').map((currency) => (
                <div
                  key={currency.code}
                  className="px-4 py-2 rounded-lg text-sm bg-gray-100 text-gray-600"
                >
                  <span className="mr-1">{currency.flag}</span>
                  1 {currency.code} = {rates ? rates.DOP / rates[currency.code] : '...'} DOP
                </div>
              ))}
              <div className="px-4 py-2 rounded-lg text-sm bg-[#22C55E]/20 text-[#22C55E] font-medium">
                <span className="mr-1">🇩🇴</span>
                1 DOP = {rates ? (1 / rates.DOP).toFixed(4) : '...'} USD
              </div>
            </div>

            {/* Refresh button */}
            <div className="mt-6 text-center">
              <Button
                onClick={fetchRates}
                disabled={loading}
                variant="outline"
                className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {t('currency.refresh') || 'Refresh Rates'}
              </Button>
              {lastUpdated && (
                <p className="text-gray-400 text-xs mt-2">
                  {t('currency.lastUpdated') || 'Last updated'}: {lastUpdated}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-gray-400 text-xs mt-4">
          {t('currency.disclaimer') || 'Exchange rates are for reference only. Actual rates may vary.'}
        </p>
      </div>
    </section>
  );
};

export default CurrencyConverter;
